import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos,
} from '../BarraDeLista';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import { data as fmtData } from '../formato';
import {
  Area, Aviso, Campo, Carregando, Dialogo, ErroDaApi, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';

/**
 * Competências — quem está treinado em cada método.
 *
 * ── O que mudou na onda 182 ─────────────────────────────────────────
 *
 * «Hoje a tela para conceder competências está muito confusa. Precisa
 *  aprovar um ensaio de cada vez para cada colaborador. Devemos criar
 *  um sistema mais intuitivo, onde possa ser selecionado vários ensaios
 *  para um colaborador ou grupo de colaboradores (em caso de
 *  treinamento em equipe).» · «A questão de executar, aprovar ou
 *  verificar não deve ser atribuída nesse módulo, e sim em usuários e
 *  acessos.» — Humberto, 11/09/2026.
 *
 * A tela virou uma MATRIZ: pessoas nas linhas, métodos nas colunas. A
 * célula diz se a pessoa tem competência no método (e em que situação)
 * ou oferece uma caixa para marcar. Clicar no código do método marca a
 * coluna inteira; clicar no nome da pessoa marca a linha. Uma gravação
 * concede tudo o que foi marcado — é o mesmo desenho da grade de
 * programação (`Programar.tsx`), pelo mesmo motivo: «quase sempre todas
 * iguais», então o gesto principal não pode custar oito cliques.
 *
 * O PAPEL não mora mais aqui. Ao lado do nome vai o que o perfil da
 * pessoa permite — L (lançar), V (verificar), A (aprovar) —, lido de
 * Usuários e acessos. Quem precisar mudar isso vai lá.
 *
 * ── Duas listas, porque são dois problemas ──────────────────────────
 *
 * A matriz responde «quem está treinado em quê». As PENDÊNCIAS
 * respondem o contrário: onde a casa está descoberta — «método sem a
 * trava ligada» é dívida de processo; «trava ligada e ninguém
 * competente» é bancada que vai parar na terça.
 */

interface Pessoa {
  id: string; nome: string; cargo: string | null; status: string;
  tem_cpf: boolean; tem_usuario: boolean;
  pode_executar: boolean; pode_verificar: boolean; pode_aprovar: boolean;
}
interface Metodo {
  id: string; codigo: string; nome: string; classe: string | null;
  exige_competencia: boolean; tem_ficha: boolean;
}
interface Celula {
  id: string; funcionario_id: string; metodo_id: string; situacao: string;
  validade: string | null; dias_para_vencer: number | null;
  tem_evidencia: boolean; treinamento_codigo: string | null;
  certificado_numero: string | null; revogada_em: string | null;
}
interface Matriz { pessoas: Pessoa[]; metodos: Metodo[]; celulas: Celula[] }

interface Competencia extends Celula {
  funcionario_nome: string; metodo_codigo: string; metodo_nome: string;
  data_autorizacao: string; autorizado_por_nome: string | null;
  motivo_revogacao: string | null; revogada_por_nome: string | null;
  observacoes: string | null; treinamento_id: string | null;
  treinamento_tema: string | null;
}

interface Pendente {
  metodo_id: string; metodo_codigo: string; metodo: string;
  autorizados: number; vencidos: number; executores: number;
  verificadores: number; aprovadores: number;
  exige_competencia: boolean; situacao: string;
}

const TOM: Record<string, Tom> = {
  vigente: 'ok', a_vencer: 'atencao', vencida: 'critico', revogada: 'neutro',
};
const SITUACAO: Record<string, string> = {
  vigente: 'Vigente', a_vencer: 'A vencer', vencida: 'Vencida',
  revogada: 'Revogada',
};
const SIGLA: Record<string, string> = {
  vigente: '✓', a_vencer: '!', vencida: '✕', revogada: '∅',
};

const chave = (f: string, m: string) => `${f}|${m}`;

export function Competencias() {
  const [matriz, setMatriz] = useState<Matriz | null>(null);
  const [pendentes, setPendentes] = useState<Pendente[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [soTrava, setSoTrava] = useState(false);
  const [soComFicha, setSoComFicha] = useState(true);
  const [selecao, setSelecao] = useState<Set<string>>(new Set());
  const [concedendo, setConcedendo] = useState(false);
  const [aberta, setAberta] = useState<Celula | null>(null);
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const [m, p] = await Promise.all([
        api<Matriz>('/competencias/matriz'),
        api<Pendente[]>('/competencias/pendentes'),
      ]);
      setMatriz(m); setPendentes(p);
    } catch (e) { setErro(e); }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  const porPar = useMemo(() => {
    const idx = new Map<string, Celula>();
    for (const c of matriz?.celulas ?? []) {
      idx.set(chave(c.funcionario_id, c.metodo_id), c);
    }
    return idx;
  }, [matriz]);

  const pessoas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return (matriz?.pessoas ?? []).filter((p) =>
      !q || p.nome.toLowerCase().includes(q)
        || (p.cargo ?? '').toLowerCase().includes(q));
  }, [matriz, busca]);

  const metodos = useMemo(() => (matriz?.metodos ?? []).filter((m) =>
    (!soTrava || m.exige_competencia) && (!soComFicha || m.tem_ficha)),
  [matriz, soTrava, soComFicha]);

  const alternar = (f: string, m: string) => {
    setSelecao((s) => {
      const n = new Set(s);
      const k = chave(f, m);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  /* Coluna inteira: marca em todos os visíveis; se todos já estavam
     marcados, desmarca. Quem já tem competência vigente fica de fora
     — renovar é gesto de célula, não de coluna. */
  const alternarColuna = (m: Metodo) => {
    setSelecao((s) => {
      const n = new Set(s);
      const alvo = pessoas.filter((p) => {
        const c = porPar.get(chave(p.id, m.id));
        return !c || c.situacao !== 'vigente';
      }).map((p) => chave(p.id, m.id));
      const todos = alvo.length > 0 && alvo.every((k) => n.has(k));
      for (const k of alvo) { if (todos) n.delete(k); else n.add(k); }
      return n;
    });
  };

  const alternarLinha = (p: Pessoa) => {
    setSelecao((s) => {
      const n = new Set(s);
      const alvo = metodos.filter((m) => {
        const c = porPar.get(chave(p.id, m.id));
        return !c || c.situacao !== 'vigente';
      }).map((m) => chave(p.id, m.id));
      const todos = alvo.length > 0 && alvo.every((k) => n.has(k));
      for (const k of alvo) { if (todos) n.delete(k); else n.add(k); }
      return n;
    });
  };

  const travaSemNinguem = pendentes.filter(
    (p) => p.situacao === 'trava_sem_ninguem');
  const semTrava = pendentes.filter((p) => p.situacao === 'nao_exige');
  const podeConceder = pode('competencia.conceder');

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Competências</h1>
          <p className="sub">
            Quem está treinado em cada método. O que a pessoa faz com isso —
            lançar, verificar, aprovar — vem do perfil, em{' '}
            <Link to="/usuarios">Usuários e acessos</Link>.
          </p>
        </div>
        <Link to="/treinamentos" className="botao">Treinamentos</Link>
      </div>

      {travaSemNinguem.length > 0 && (
        <Aviso tipo="erro" titulo="Trava ligada e ninguém competente">
          {travaSemNinguem.length} método(s) exigem competência e não têm
          ninguém válido. O banco vai recusar o ensaio na hora de gravar —
          não na hora de programar.
          <ul className="lista-simples">
            {travaSemNinguem.slice(0, 8).map((p) => (
              <li key={p.metodo_id}>
                <b>{p.metodo_codigo}</b> · {p.metodo}
                {p.vencidos > 0 && ` — ${p.vencidos} competência(s) vencida(s)`}
              </li>
            ))}
          </ul>
        </Aviso>
      )}

      {erro ? <ErroDaApi erro={erro} />
        : !matriz ? <Carregando />
        : (
          <div className="cartao">
            <div className="cartao-cabeca">
              <h2>Matriz de competências</h2>
              <span className="sub">pessoas por nome · métodos com a trava primeiro</span>
            </div>

            {/*
              * A barra do padrão (onda 273): a busca e as duas caixas de
              * seleção soltas viraram a busca e as alternâncias da barra.
              * A matriz continua — é a forma certa de mostrar pessoa ×
              * método.
              */}
            <BarraDeLista rotulo="Filtros da matriz">
              <BuscaDaBarra valor={busca} aoMudar={setBusca}
                placeholder="Pessoa: nome ou cargo" rotulo="Buscar pessoa" />
              <AlternarDaBarra ligado={soComFicha} aoMudar={setSoComFicha}>
                {soComFicha ? '✓ Métodos com ficha de lançamento' : 'Métodos com ficha de lançamento'}
              </AlternarDaBarra>
              <AlternarDaBarra ligado={soTrava} aoMudar={setSoTrava}>
                {soTrava ? '✓ Métodos com a trava ligada' : 'Métodos com a trava ligada'}
              </AlternarDaBarra>
            </BarraDeLista>
            <FiltrosAtivos
              itens={busca ? [{ chave: 'q', texto: `Pessoa: ${busca}`, tirar: () => setBusca('') }] : []}
              aoLimpar={() => setBusca('')}
              contagem={`${pessoas.length} ${pessoas.length === 1 ? 'pessoa' : 'pessoas'} × ${metodos.length} ${metodos.length === 1 ? 'método' : 'métodos'}`} />

            {pessoas.length === 0 || metodos.length === 0 ? (
              <Vazio>Nada para mostrar com estes filtros.</Vazio>
            ) : (
              <div className="rola">
                <table className="dados grade-programacao grade-competencia">
                  <thead>
                    <tr>
                      <th scope="col" className="canto">
                        <span className="sub">
                          clique no nome ou no código para marcar a linha
                          ou a coluna
                        </span>
                      </th>
                      {metodos.map((m) => (
                        <th key={m.id} scope="col" className="col-metodo">
                          <button type="button" className="cabeca-metodo"
                            title={`${m.nome}${m.exige_competencia
                              ? ' — trava ligada' : ' — trava desligada'}`}
                            disabled={!podeConceder}
                            onClick={() => alternarColuna(m)}>
                            {m.codigo}
                            {m.exige_competencia && (
                              <span className="trava" aria-label="trava ligada">
                                trava
                              </span>
                            )}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pessoas.map((p) => (
                      <tr key={p.id}>
                        <th scope="row">
                          <button type="button" className="cabeca-amostra"
                            disabled={!podeConceder}
                            onClick={() => alternarLinha(p)}>
                            {p.nome}
                          </button>
                          <span className="sub">
                            {p.cargo ?? 'sem cargo'}
                            {' '}<PapeisDoPerfil p={p} />
                          </span>
                        </th>
                        {metodos.map((m) => {
                          const c = porPar.get(chave(p.id, m.id));
                          const k = chave(p.id, m.id);
                          const marcada = selecao.has(k);
                          if (c && !marcada) {
                            return (
                              <td key={m.id} className="celula-grade">
                                <button type="button"
                                  className={`ja-programado comp ${c.situacao}`}
                                  title={`${SITUACAO[c.situacao]}${c.validade
                                    ? ' · até ' + fmtData(c.validade) : ''}`}
                                  aria-label={`${p.nome} · ${m.codigo}: ${
                                    SITUACAO[c.situacao]}`}
                                  onClick={() => setAberta(c)}>
                                  {SIGLA[c.situacao] ?? '·'}
                                </button>
                              </td>
                            );
                          }
                          return (
                            <td key={m.id} className="celula-grade">
                              <label className={`caixa-grade${
                                c ? ' previsto' : ''}`}
                                title={c ? 'renovar a competência' : 'conceder'}>
                                <input type="checkbox" checked={marcada}
                                  disabled={!podeConceder}
                                  aria-label={`${p.nome} · ${m.codigo}${
                                    c ? ' (renovar)' : ''}`}
                                  onChange={() => alternar(p.id, m.id)} />
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="legenda-grade">
              <b>✓</b> vigente · <b>!</b> a vencer (30 dias — janela de
              leitura, não critério da casa) · <b>✕</b> vencida ·{' '}
              <b>∅</b> revogada · <b>L V A</b> ao lado do cargo = o que o
              perfil permite (lançar, verificar, aprovar). Clique numa
              célula preenchida para ver a origem, revogar ou renovar.
            </p>

            {selecao.size > 0 && (
              <div className="barra-selecao">
                <span><b>{selecao.size}</b> par(es) marcado(s)</span>
                <button type="button" onClick={() => setSelecao(new Set())}>
                  Limpar
                </button>
                <button type="button" className="principal"
                  onClick={() => setConcedendo(true)}>
                  Conceder {selecao.size} competência(s)…
                </button>
              </div>
            )}
          </div>
        )}

      {semTrava.length > 0 && (
        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>Métodos sem a trava ligada</h2>
            <span className="sub">{semTrava.length} método(s)</span>
          </div>
          <p className="sub">
            Nestes métodos qualquer pessoa lança, verifica e aprova. Não é
            defeito: a trava vem <b>depois</b> do treinamento cadastrado. O
            que esta lista impede é que «depois» vire nunca.
          </p>
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Método</th>
                  <th scope="col">Competentes</th>
                  <th scope="col">Lançam · verificam · aprovam</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {semTrava.slice(0, 60).map((p) => (
                  <tr key={p.metodo_id}>
                    <th scope="row">
                      <span className="mono">{p.metodo_codigo}</span>
                      <span className="sub">{p.metodo}</span>
                    </th>
                    <td>
                      {p.autorizados > 0
                        ? `${p.autorizados} pessoa(s)`
                        : <span className="nada">ninguém</span>}
                    </td>
                    <td>{p.executores} · {p.verificadores} · {p.aprovadores}</td>
                    <td>
                      {podeConceder && p.autorizados > 0 && (
                        <BotaoTrava metodoId={p.metodo_id}
                          codigo={p.metodo_codigo} aoPronto={carregar} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {concedendo && matriz && (
        <DialogoConcederLote
          pares={[...selecao].map((k) => {
            const [f, m] = k.split('|');
            return {
              pessoa: matriz.pessoas.find((p) => p.id === f)!,
              metodo: matriz.metodos.find((x) => x.id === m)!,
            };
          })}
          aoFechar={() => setConcedendo(false)}
          aoPronto={() => {
            setConcedendo(false); setSelecao(new Set()); void carregar();
          }}
        />
      )}
      {aberta && (
        <DialogoCompetencia
          celula={aberta}
          aoFechar={() => setAberta(null)}
          aoRenovar={() => {
            alternar(aberta.funcionario_id, aberta.metodo_id);
            setAberta(null);
          }}
          aoPronto={() => { setAberta(null); void carregar(); }}
        />
      )}
    </div>
  );
}

/**
 * L V A — o que o PERFIL da pessoa permite.
 *
 * §18: identificação visual sem depender só de cor. As letras estão lá
 * e o `title` diz por extenso. Sem usuário, sem letra nenhuma: a
 * pessoa existe no quadro, mas não assina ensaio.
 */
function PapeisDoPerfil({ p }: { p: Pessoa }) {
  if (!p.tem_usuario) {
    return <span className="papeis sem" title="sem usuário ativo">sem acesso</span>;
  }
  return (
    <span className="papeis" title={[
      p.pode_executar ? 'lança' : null,
      p.pode_verificar ? 'verifica' : null,
      p.pode_aprovar ? 'aprova' : null,
    ].filter(Boolean).join(' · ') || 'não lança, não verifica, não aprova'}>
      <b className={p.pode_executar ? 'tem' : ''}>L</b>
      <b className={p.pode_verificar ? 'tem' : ''}>V</b>
      <b className={p.pode_aprovar ? 'tem' : ''}>A</b>
    </span>
  );
}

function BotaoTrava({ metodoId, codigo, aoPronto }: {
  metodoId: string; codigo: string; aoPronto(): void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const ligar = async () => {
    setOcupado(true); setErro(null);
    try {
      await api(`/competencias/metodos/${metodoId}/exigir`, {
        metodo: 'POST',
        corpo: {
          exige: true,
          motivo: `Trava de competência ligada em ${codigo} pela tela de `
            + 'competências, com pessoal já treinado.',
        },
      });
      aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <>
      <button type="button" className="ligacao" disabled={ocupado}
        onClick={() => { void ligar(); }}>
        {ocupado ? 'ligando…' : 'ligar a trava'}
      </button>
      {erro && <span className="sub">{erro}</span>}
    </>
  );
}

interface Par { pessoa: Pessoa; metodo: Metodo }
interface ResultadoLote {
  concedidas: number; renovadas: number; recusadas: number;
  pares: { funcionario_id: string; metodo_id: string; situacao: string;
    motivo?: string }[];
}

function DialogoConcederLote({ pares, aoFechar, aoPronto }: {
  pares: Par[]; aoFechar(): void; aoPronto(): void;
}) {
  const [desde, setDesde] = useState(new Date().toLocaleDateString('en-CA'));
  const [validade, setValidade] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState<ResultadoLote | null>(null);

  const pessoas = [...new Map(pares.map((p) => [p.pessoa.id, p.pessoa])).values()];
  const metodos = [...new Map(pares.map((p) => [p.metodo.id, p.metodo])).values()];

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      /*
       * O lote vai como pessoas × métodos só quando a seleção É um
       * retângulo cheio. Quando não é (marcou três células soltas), cada
       * par vai como um lote de 1 × 1 — a rota é a mesma, e ninguém
       * ganha competência que não marcou.
       */
      const cheio = pares.length === pessoas.length * metodos.length;
      const corpoBase = {
        dataAutorizacao: desde, validade: validade || undefined,
        observacoes: observacoes || undefined,
      };
      let r: ResultadoLote;
      if (cheio) {
        r = await api<ResultadoLote>('/competencias/lote', {
          metodo: 'POST',
          corpo: { ...corpoBase, funcionarios: pessoas.map((p) => p.id),
            metodos: metodos.map((m) => m.id) },
        });
      } else {
        r = { concedidas: 0, renovadas: 0, recusadas: 0, pares: [] };
        for (const par of pares) {
          const x = await api<ResultadoLote>('/competencias/lote', {
            metodo: 'POST',
            corpo: { ...corpoBase, funcionarios: [par.pessoa.id],
              metodos: [par.metodo.id] },
          });
          r.concedidas += x.concedidas; r.renovadas += x.renovadas;
          r.recusadas += x.recusadas; r.pares.push(...x.pares);
        }
      }
      setResultado(r);
      if (r.recusadas === 0) aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  const nome = (id: string) => pessoas.find((p) => p.id === id)?.nome ?? id;
  const codigo = (id: string) => metodos.find((m) => m.id === id)?.codigo ?? id;

  return (
    <Dialogo aberto titulo="Conceder competências" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {resultado ? (
        <>
          <Aviso tipo={resultado.recusadas ? 'atencao' : 'ok'}
            titulo={`${resultado.concedidas} concedida(s) · ${
              resultado.renovadas} renovada(s) · ${
              resultado.recusadas} recusada(s)`}>
            {resultado.recusadas > 0 && (
              <ul className="lista-simples">
                {resultado.pares.filter((p) => p.situacao === 'recusada')
                  .map((p) => (
                    <li key={`${p.funcionario_id}${p.metodo_id}`}>
                      <b>{nome(p.funcionario_id)}</b> · {codigo(p.metodo_id)}:
                      {' '}{p.motivo}
                    </li>
                  ))}
              </ul>
            )}
          </Aviso>
          <div className="acoes">
            <button type="button" className="principal" onClick={aoPronto}>
              Fechar
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="sub">
            <b>{pessoas.length}</b> pessoa(s) × <b>{metodos.length}</b>{' '}
            método(s) — {pares.length} competência(s).
          </p>
          <div className="resumo-lote">
            <div>
              <span className="rotulo">Pessoas</span>
              <ul className="lista-simples">
                {pessoas.map((p) => <li key={p.id}>{p.nome}</li>)}
              </ul>
            </div>
            <div>
              <span className="rotulo">Métodos</span>
              <ul className="lista-simples">
                {metodos.map((m) => (
                  <li key={m.id}><span className="mono">{m.codigo}</span>
                    {' '}<span className="sub">{m.nome}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="linha-campos">
            <Campo rotulo="Competente desde" type="date" value={desde}
              onChange={(ev) => setDesde(ev.target.value)} />
            <Campo rotulo="Validade (opcional)" type="date" value={validade}
              dica="Em branco = sem prazo"
              onChange={(ev) => setValidade(ev.target.value)} />
          </div>
          <Area rotulo="Evidência / observação" rows={2} value={observacoes}
            dica="Certificado externo, experiência comprovada… Treinamento da casa? Lance em Treinamentos: concluir concede sozinho, com certificado."
            onChange={(ev) => setObservacoes(ev.target.value)} />
          <p className="sub">
            Quem assina é você — o sistema não deixa escolher o autorizador,
            e recusa a sua própria linha se ela estiver marcada (§15).
          </p>
          <div className="acoes">
            <button type="button" onClick={aoFechar}>Cancelar</button>
            <button type="button" className="principal"
              disabled={ocupado || !desde}
              onClick={() => { void gravar(); }}>
              {ocupado ? 'Gravando…' : `Conceder ${pares.length}`}
            </button>
          </div>
        </>
      )}
    </Dialogo>
  );
}

/**
 * A célula aberta: de onde veio a competência, e o que se pode fazer.
 *
 * Revogar não apaga — fica com data, nome e motivo. O ensaio que esta
 * pessoa assinou no mês passado continua tendo sido assinado por
 * alguém competente NAQUELE dia.
 */
function DialogoCompetencia({ celula, aoFechar, aoRenovar, aoPronto }: {
  celula: Celula; aoFechar(): void; aoRenovar(): void; aoPronto(): void;
}) {
  const [c, setC] = useState<Competencia | null>(null);
  const [motivo, setMotivo] = useState('');
  const [revogando, setRevogando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const { pode } = useSessao();

  useEffect(() => {
    api<Competencia[]>(`/competencias?funcionario=${celula.funcionario_id}`
      + `&metodo=${celula.metodo_id}`)
      .then((l) => setC(l[0] ?? null))
      .catch((e) => setErro(mensagemDoErro(e)));
  }, [celula]);

  const revogar = async () => {
    setOcupado(true); setErro(null);
    try {
      await api(`/competencias/${celula.id}/revogar`,
        { metodo: 'POST', corpo: { motivo } });
      aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Competência" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {!c ? <Carregando /> : (
        <>
          <p>
            <b>{c.funcionario_nome}</b> ·{' '}
            <span className="mono">{c.metodo_codigo}</span>{' '}
            <span className="sub">{c.metodo_nome}</span>
          </p>
          <p>
            <Selo tom={TOM[c.situacao] ?? 'neutro'}>
              {SITUACAO[c.situacao] ?? c.situacao}
            </Selo>
            {' '}desde {fmtData(c.data_autorizacao)}
            {c.validade ? ` · válida até ${fmtData(c.validade)}` : ' · sem prazo'}
            {c.autorizado_por_nome && ` · concedida por ${c.autorizado_por_nome}`}
          </p>
          {c.treinamento_id ? (
            <p className="sub">
              Evidência: treinamento{' '}
              <Link to={`/treinamentos/${c.treinamento_id}`}>
                {c.treinamento_codigo}
              </Link>
              {c.treinamento_tema && ` — ${c.treinamento_tema}`}
              {c.certificado_numero && ` · certificado ${c.certificado_numero}`}
            </p>
          ) : (
            <p className="sub">
              {c.tem_evidencia ? 'Evidência anexada.' : 'Sem evidência anexada.'}
              {c.observacoes && ` ${c.observacoes}`}
            </p>
          )}
          {c.revogada_em && (
            <Aviso tipo="info">
              Revogada em {fmtData(c.revogada_em)}
              {c.revogada_por_nome && ` por ${c.revogada_por_nome}`}
              {c.motivo_revogacao && ` — ${c.motivo_revogacao}`}
            </Aviso>
          )}

          {revogando ? (
            <>
              <Area rotulo="Por que retirar?" rows={3} value={motivo}
                placeholder="mudou de função, treinamento vencido sem renovação…"
                onChange={(ev) => setMotivo(ev.target.value)} />
              <div className="acoes">
                <button type="button" onClick={() => setRevogando(false)}>
                  Voltar
                </button>
                <button type="button" className="principal perigo"
                  disabled={ocupado || motivo.trim().length < 10}
                  onClick={() => { void revogar(); }}>
                  {ocupado ? 'Gravando…' : 'Revogar'}
                </button>
              </div>
            </>
          ) : (
            <div className="acoes">
              <button type="button" onClick={aoFechar}>Fechar</button>
              {pode('competencia.conceder') && (
                <>
                  {!c.revogada_em && (
                    <button type="button" onClick={() => setRevogando(true)}>
                      Revogar…
                    </button>
                  )}
                  <button type="button" className="principal" onClick={aoRenovar}>
                    {c.revogada_em ? 'Conceder de novo' : 'Marcar para renovar'}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </Dialogo>
  );
}
